import type {
  RouteWithSubPath,
  SubRoute,
} from '@ipen-portal/plugin-common-react';

import {
  type Entity,
  getCompoundEntityRef,
  isApiEntity,
  parseEntityRef,
} from '@backstage/catalog-model';
import { createExtensionInput, useApi } from '@backstage/frontend-plugin-api';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { techdocsStorageApiRef } from '@backstage/plugin-techdocs-react';
import { Icon } from '@iconify/react';
import { isSolutionModuleEntity } from '@ipen-portal/ipen-entity-model';
import { isEnergyNetworkDomainEntity } from '@ipen-portal/plugin-common';
import {
  DynamicDocsNavigationSubPathBlueprint,
  DynamicEntityRouteBlueprint,
} from '@ipen-portal/plugin-common-react';
import { useAsync } from 'react-use';

import { TECHDOCS_EXTERNAL_ANNOTATION } from '../constants';
import { EmbeddedDocsRouter } from '../vendor/EmbeddedDocsRouter';

type MkDocsNavStructure = {
  children?: MkDocsNavStructure[];
  icon?: string;
  indexTitle?: string;
  path: string;
  title: string;
};

const NoDocFallback: SubRoute[] = [
  {
    children: <EmbeddedDocsRouter />,
    icon: <Icon icon="material-symbols:description-outline" />,
    path: '/docs',
    title: 'Documentation',
  },
];

const showDocs = (entity: Entity) =>
  !isEnergyNetworkDomainEntity(entity)
  && !entity.metadata.tags?.includes('hide-docs')
  && !isApiEntity(entity)
  && !isSolutionModuleEntity(entity);

const recursivelyFindChildrenByPath = (
  nav: MkDocsNavStructure[],
  path: string,
): MkDocsNavStructure[] | undefined => {
  if (!path) return nav;

  for (const item of nav) {
    if (item.path === path) {
      return item.children;
    }
    if (item.children?.length) {
      const found = recursivelyFindChildrenByPath(item.children, path);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

const createRouteFromMkDocsNav = (
  navItem: MkDocsNavStructure,
  basePath: string,
) =>
  ({
    icon: navItem.icon
      ? (
          <img alt="icon" src={`${basePath}/${navItem.icon}`} width={24} />
        )
      : (
          <Icon icon="material-symbols:description-outline" />
        ),
    subPath: navItem.path.startsWith('./')
      ? navItem.path.slice(2)
      : navItem.path,
    title: navItem.title,
  }) satisfies RouteWithSubPath;

export const DynamicDocs = DynamicEntityRouteBlueprint.makeWithOverrides({
  factory: (originalFactory, { inputs }) => {
    const subPathDefinitions = inputs.pathResolver.map(item => ({
      filter: item.get(
        DynamicDocsNavigationSubPathBlueprint.dataRefs.entityFilter,
      ),
      resolveSubPath: item.get(
        DynamicDocsNavigationSubPathBlueprint.dataRefs.subPath,
      ),
    }));
    return originalFactory({
      filter: showDocs,
      useDynamicSubRoutes: () => {
        const { entity } = useAsyncEntity();

        const techdocsStorageApi = useApi(techdocsStorageApiRef);

        const { loading, value: dynamicRoutes = NoDocFallback } =
          useAsync(async () => {
            if (!entity) {
              return;
            }

            const compoundEntityRef = getCompoundEntityRef(entity);

            const syncResult =
              await techdocsStorageApi.syncEntityDocs(compoundEntityRef);

            if (syncResult !== 'cached' && syncResult !== 'updated') {
              return;
            }

            const resolvedSubPath = subPathDefinitions
              .find(r => r.filter(entity))
              ?.resolveSubPath(entity);

            const entityRef =
              entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION]
              ?? compoundEntityRef;

            const { kind, name, namespace } = parseEntityRef(entityRef);

            const basePath = `/static-docs/${namespace}/${kind.toLowerCase()}/${name}`;

            const response = await fetch(`${basePath}/navigation.json`);

            if (!response.ok) {
              return;
            }

            const mkDocsNav =
              (await response.json()) as MkDocsNavStructure[];

            const navChildren =
              recursivelyFindChildrenByPath(mkDocsNav, resolvedSubPath ?? '')
              ?? mkDocsNav;

            return [
              {
                children: <EmbeddedDocsRouter />,
                path: '/docs',
                tabs: navChildren
                  .filter(navItem => navItem.path)
                  .map(navItem => createRouteFromMkDocsNav(navItem, basePath)),
              } satisfies SubRoute,
            ];
          }, [techdocsStorageApi, entity]);

        return { dynamicRoutes, loading };
      },
    });
  },
  inputs: {
    pathResolver: createExtensionInput([
      DynamicDocsNavigationSubPathBlueprint.dataRefs.subPath,
      DynamicDocsNavigationSubPathBlueprint.dataRefs.entityFilter,
    ]),
  },
  name: 'dynamic-docs',
});
